<template>
  <div class="blog-post-container">
    <!-- Back to Blog Link -->
    <div class="back-link-container">
      <router-link to="/blog" class="back-link">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Blog
      </router-link>
    </div>

    <!-- Main Content with TOC -->
    <div class="blog-content-wrapper">
      <!-- Article Content -->
      <article v-if="post" class="blog-article max-w-4xl mx-auto">
        <h1 class="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
          {{ post.title }}
        </h1>

        <div class="flex items-center article-meta mb-8">
          <span>{{ post.date }}</span>
          <span class="mx-2">•</span>
          <span>{{ post.readingTime }} min read</span>
        </div>

        <img
          v-if="post.image"
          :src="post.image"
          :alt="post.title"
          class="w-full h-64 object-cover rounded-lg mb-8"
        />

        <template v-for="(section, index) in parsedContent" :key="index">
          <DynamicComponent
            v-if="section.type === 'component'"
            :type="section.componentType"
            :props="section.props"
            class="my-8"
            @image-register="registerImage"
            @image-click="openGallery"
          />
          <div
            v-else
            v-html="renderMarkdown(section.content)"
            class="markdown-content"
          ></div>
        </template>
      </article>

      <!-- Post Not Found -->
      <div v-if="!post" class="text-center py-12 max-w-4xl mx-auto">
        <p class="text-xl text-gray-600">Post not found</p>
        <router-link
          to="/blog"
          class="text-blue-600 hover:text-blue-800 mt-4 inline-block"
        >
          Return to Blog
        </router-link>
      </div>
    </div>

    <!-- Table of Contents - Fixed Positioned -->
    <aside class="toc-sidebar">
      <TableOfContents :headings="tableOfContents" />
    </aside>

    <!-- Klaviyo Subscribe Form - Separate Fixed Container -->
    <aside class="klaviyo-sidebar">
      <div class="klaviyo-form-container">
        <KlaviyoForm />
      </div>
    </aside>

    <!-- Blog Post Gallery -->
    <BlogPostGallery :images="galleryImages" ref="blogPostGallery" />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { marked } from "marked";
import { getPostBySlug } from "@/utils/blog";
import DynamicComponent from "@/components/blog/DynamicComponent.vue";
import { createLazyLoadObserver, debounce } from "@/utils/imageOptimizer";
import TableOfContents from "@/components/blog/TableOfContents.vue";
import BlogPostGallery from "@/components/blog/BlogPostGallery.vue";
import KlaviyoForm from "@/components/blog/KlaviyoForm.vue";

export default {
  name: "BlogPostPage",
  components: {
    DynamicComponent,
    TableOfContents,
    BlogPostGallery,
    KlaviyoForm,
  },
  setup() {
    const route = useRoute();
    const post = ref(null);

    const renderMarkdown = (content) => {
      // Remove component markers but keep noscript content
      const cleanContent = content.replace(/<!--component:[\s\S]*?-->/g, "");

      // Render markdown first
      const renderedContent = marked(cleanContent);

      // Then add IDs to headings using post-processing
      try {
        return renderedContent.replace(
          /<h([1-4])>(.*?)<\/h\1>/g,
          (match, level, text) => {
            // Ensure text is a string and handle HTML entities
            const textStr = String(text || "").trim();
            if (!textStr) {
              return match; // Return original if no text
            }

            // Strip HTML tags for ID generation (same as TOC)
            const cleanTextForId = textStr
              .replace(/<[^>]*>/g, "") // Remove HTML tags
              .replace(/\*\*(.*?)\*\*/g, "$1") // Remove **bold** markdown
              .replace(/\*(.*?)\*/g, "$1") // Remove *italic* markdown
              .replace(/`(.*?)`/g, "$1") // Remove `code` markdown
              .replace(/&amp;/g, "&") // Decode common HTML entities
              .replace(/&lt;/g, "<")
              .replace(/&gt;/g, ">")
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .trim();

            const id = cleanTextForId
              .toLowerCase()
              .replace(/[^\w\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/-+/g, "-");

            // Ensure ID is not empty
            if (!id) {
              return match; // Return original if no valid ID
            }

            return `<h${level} id="${id}">${text}</h${level}>`;
          }
        );
      } catch (error) {
        console.warn("Error adding IDs to headings:", error);
        return renderedContent; // Fallback to rendered content without IDs
      }
    };

    const parseComponent = (componentStr) => {
      try {
        console.log("BlogPostPage: Parsing component string:", componentStr);

        // Extract the JSON part from the comment
        const match = componentStr.match(/<!--component:([\s\S]*?)-->/);
        if (!match) {
          console.warn(
            "BlogPostPage: No component match found in:",
            componentStr
          );
          return null;
        }

        const jsonStr = match[1].trim();
        console.log("BlogPostPage: Extracted JSON string:", jsonStr);

        const componentData = JSON.parse(jsonStr);
        console.log("BlogPostPage: Parsed component data:", componentData);

        return {
          type: "component",
          componentType: componentData.type,
          props: componentData.props,
        };
      } catch (e) {
        console.error(
          "BlogPostPage: Failed to parse component:",
          e,
          componentStr
        );
        return null;
      }
    };

    const parsedContent = computed(() => {
      if (!post.value?.content) return [];

      const content = post.value.content;
      console.log(
        "BlogPostPage: Parsing content:",
        content.substring(0, 200) + "..."
      );

      // Split by HTML comments
      const parts = content.split(/(<!--component:[\s\S]*?-->)/);
      console.log("BlogPostPage: Split parts:", parts.length);

      const sections = [];
      parts.forEach((part) => {
        const trimmedPart = part.trim();
        if (!trimmedPart) return;

        if (trimmedPart.startsWith("<!--component:")) {
          const componentSection = parseComponent(trimmedPart);
          if (componentSection) {
            console.log("BlogPostPage: Parsed component:", componentSection);
            sections.push(componentSection);
          } else {
            console.warn(
              "BlogPostPage: Failed to parse component:",
              trimmedPart
            );
          }
        } else {
          sections.push({
            type: "text",
            content: trimmedPart,
          });
        }
      });

      console.log("BlogPostPage: Final sections:", sections);
      return sections;
    });

    const tableOfContents = computed(() => {
      if (!post.value?.content) return [];

      const headings = [];

      // Extract headings from markdown content using regex
      const headingRegex = /^(#{1,3})\s+(.+)$/gm;
      let match;

      while ((match = headingRegex.exec(post.value.content)) !== null) {
        const level = match[1].length;
        const text = match[2].trim();

        // Strip HTML tags from the text for display
        const cleanText = text
          .replace(/<[^>]*>/g, "") // Remove HTML tags
          .replace(/\*\*(.*?)\*\*/g, "$1") // Remove **bold** markdown
          .replace(/\*(.*?)\*/g, "$1") // Remove *italic* markdown
          .replace(/`(.*?)`/g, "$1") // Remove `code` markdown
          .replace(/&amp;/g, "&") // Decode common HTML entities
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .trim();

        const id = cleanText
          .toLowerCase()
          .replace(/[^\w\s-]/g, "") // Remove special characters
          .replace(/\s+/g, "-") // Replace spaces with hyphens
          .replace(/-+/g, "-"); // Replace multiple hyphens with single

        headings.push({
          id,
          text: cleanText, // Use clean text without HTML
          level,
        });
      }

      return headings;
    });

    const wrapLeadingEmojisInLists = async () => {
      await nextTick();
      const listItems = document.querySelectorAll(
        ".markdown-content li:not([data-emoji-processed])"
      );
      listItems.forEach((li) => {
        const firstNode = li.firstChild;
        if (!firstNode || firstNode.nodeType !== Node.TEXT_NODE) return;
        const text = firstNode.textContent || "";
        const match = text.match(/^(\p{Extended_Pictographic}+)(\s*)/u);
        if (!match) return;
        const [, emoji, spacer] = match;
        const remainder = text.slice(match[0].length);

        const span = document.createElement("span");
        span.className = "emoji-lead";
        span.textContent = `${emoji}${spacer}`;

        const remainderNode = document.createTextNode(remainder);
        li.replaceChild(remainderNode, firstNode);
        li.insertBefore(span, remainderNode);
        li.setAttribute("data-emoji-processed", "true");
      });
    };

    const setupCodeCopyButtons = async () => {
      await nextTick();
      const codeBlocks = document.querySelectorAll(".markdown-content pre");
      codeBlocks.forEach((pre) => {
        // Remove existing copy button if any
        const existingButton = pre.querySelector(".copy-button");
        if (existingButton) existingButton.remove();

        // Create copy button
        const copyButton = document.createElement("button");
        copyButton.className = "copy-button";
        copyButton.textContent = "Copy";
        copyButton.style.cssText = `
          position: absolute;
          top: 12px;
          right: 12px;
          background-color: rgb(55 65 81);
          color: rgb(209 213 219);
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
          z-index: 10;
          border: none;
        `;

        // Add to pre element
        pre.appendChild(copyButton);

        // Add hover effects
        copyButton.addEventListener("mouseenter", () => {
          copyButton.style.backgroundColor = "rgb(75 85 99)";
          copyButton.style.color = "white";
        });

        copyButton.addEventListener("mouseleave", () => {
          copyButton.style.backgroundColor = "rgb(55 65 81)";
          copyButton.style.color = "rgb(209 213 219)";
        });

        // Add click functionality
        copyButton.addEventListener("click", async () => {
          const code = pre.querySelector("code") || pre;
          const textToCopy = code.textContent || pre.textContent;

          try {
            await navigator.clipboard.writeText(textToCopy);
            copyButton.textContent = "Copied!";
            copyButton.style.backgroundColor = "rgb(16 185 129)";
            copyButton.style.color = "white";

            setTimeout(() => {
              copyButton.textContent = "Copy";
              copyButton.style.backgroundColor = "rgb(55 65 81)";
              copyButton.style.color = "rgb(209 213 219)";
            }, 2000);
          } catch (err) {
            copyButton.textContent = "Failed";
            copyButton.style.backgroundColor = "rgb(239 68 68)";
            copyButton.style.color = "white";

            setTimeout(() => {
              copyButton.textContent = "Copy";
              copyButton.style.backgroundColor = "rgb(55 65 81)";
              copyButton.style.color = "rgb(209 213 219)";
            }, 2000);
          }
        });
      });
    };

    const setupGlobalImageLazyLoading = async () => {
      await nextTick();

      // Find all images in markdown content that don't already have lazy loading
      const images = document.querySelectorAll(
        ".markdown-content img:not([data-lazy-processed])"
      );

      images.forEach((img) => {
        // Mark as processed
        img.setAttribute("data-lazy-processed", "true");

        // Skip if already has src
        if (img.src && img.src !== window.location.href) return;

        // Create a wrapper for the image if it doesn't have one
        let wrapper = img.parentElement;
        if (!wrapper || !wrapper.classList.contains("lazy-image-wrapper")) {
          wrapper = document.createElement("div");
          wrapper.className = "lazy-image-wrapper";
          img.parentNode.insertBefore(wrapper, img);
          wrapper.appendChild(img);
        }

        // Add loading state
        wrapper.classList.add("loading");

        // Set up intersection observer for this image using utility
        const observer = createLazyLoadObserver(
          (entry) => {
            const targetImg = entry.target;
            const targetWrapper = targetImg.closest(".lazy-image-wrapper");

            if (targetWrapper) {
              targetWrapper.classList.remove("loading");
              targetWrapper.classList.add("loaded");
            }

            // Start loading the image
            if (targetImg.dataset.src) {
              targetImg.src = targetImg.dataset.src;
              targetImg.removeAttribute("data-src");
            }

            // Disconnect observer after loading
            observer.disconnect();
          },
          { rootMargin: "100px" }
        );

        observer.observe(img);
      });
    };

    // Debounced version for better performance during scrolling
    const debouncedImageSetup = debounce(setupGlobalImageLazyLoading, 100);

    const setupPathFormatting = async () => {
      await nextTick();
      const paragraphs = document.querySelectorAll(".markdown-content p");

      paragraphs.forEach((p) => {
        const text = p.innerHTML;
        console.log(
          "Path formatting: Checking paragraph:",
          text.substring(0, 100)
        );

        // Look for path patterns after "go to:" or "navigate to:" markers (case insensitive)
        // Handle different arrow characters: →, ->, →
        const pathPattern =
          /(go to|navigate to):\s*([A-Za-z0-9\s]+)\s*(?:→|->)\s*([A-Za-z0-9\s]+)(?:\s*(?:→|->)\s*([A-Za-z0-9\s]+))?(?:\s*(?:→|->)\s*([A-Za-z0-9\s]+))?/gi;

        if (pathPattern.test(text)) {
          console.log("Path formatting: Found match in:", text);
          // Reset the regex
          pathPattern.lastIndex = 0;

          const newText = text.replace(
            pathPattern,
            (match, marker, part1, part2, part3, part4) => {
              console.log(
                "Path formatting: Replacing with:",
                marker,
                part1,
                part2,
                part3,
                part4
              );
              if (part4) {
                // Four-part path
                return `${marker}: <span class="path-arrow"><span>${part1.trim()}</span><span class="separator">→</span><span>${part2.trim()}</span><span class="separator">→</span><span>${part3.trim()}</span><span class="separator">→</span><span>${part4.trim()}</span></span>`;
              } else if (part3) {
                // Three-part path
                return `${marker}: <span class="path-arrow"><span>${part1.trim()}</span><span class="separator">→</span><span>${part2.trim()}</span><span class="separator">→</span><span>${part3.trim()}</span></span>`;
              } else {
                // Two-part path
                return `${marker}: <span class="path-arrow"><span>${part1.trim()}</span><span class="separator">→</span><span>${part2.trim()}</span></span>`;
              }
            }
          );

          p.innerHTML = newText;
        } else {
          console.log("Path formatting: No match found in:", text);
        }
      });
    };

    const updateMetaTags = () => {
      if (!post.value) return;

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.setAttribute("name", "description");
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute("content", post.value.meta.description);

      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute("content", post.value.meta.keywords);

      // Add robots meta tag for better crawl control
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "index, follow");

      // Add canonical link to prevent duplicate content issues
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", window.location.href);

      // Add structured data
      let scriptTag = document.querySelector("#structured-data");
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.setAttribute("id", "structured-data");
        scriptTag.setAttribute("type", "application/ld+json");
        document.head.appendChild(scriptTag);
      }

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.value.title,
        description: post.value.meta.description,
        datePublished: new Date(post.value.date).toISOString(),
        dateModified: new Date().toISOString(),
        author: {
          "@type": "Organization",
          name: "Better Insights",
          url: "https://betterinsights.co",
        },
        publisher: {
          "@type": "Organization",
          name: "Better Insights",
          url: "https://betterinsights.co",
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": window.location.href,
        },
      };

      scriptTag.textContent = JSON.stringify(structuredData);
    };

    watch(
      post,
      async () => {
        updateMetaTags();
        await wrapLeadingEmojisInLists();
        await setupCodeCopyButtons();
        debouncedImageSetup(); // Use debounced version
        await setupPathFormatting();
      },
      { immediate: true }
    );

    onMounted(async () => {
      post.value = await getPostBySlug(route.params.slug);
      await wrapLeadingEmojisInLists();
      await setupCodeCopyButtons();
      debouncedImageSetup(); // Use debounced version
      await setupPathFormatting();

      // Setup scroll detection for mobile back link
      setupScrollDetection();
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    // Methods for scroll detection
    const lastScrollTop = ref(0);

    const setupScrollDetection = () => {
      // Only setup scroll detection on mobile
      if (window.innerWidth <= 1024) {
        window.addEventListener("scroll", handleScroll);
      }
    };

    const handleScroll = () => {
      // Only handle scroll on mobile
      if (window.innerWidth <= 1024) {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const isScrollingUp = scrollTop < lastScrollTop.value;

        // Show back link when scrolling up and not at the very top
        if (isScrollingUp && scrollTop > 100) {
          showBackLink();
        } else {
          hideBackLink();
        }

        lastScrollTop.value = scrollTop;
      }
    };

    const showBackLink = () => {
      const backLinkContainer = document.querySelector(".back-link-container");
      if (backLinkContainer) {
        backLinkContainer.classList.add("show");
      }
    };

    const hideBackLink = () => {
      const backLinkContainer = document.querySelector(".back-link-container");
      if (backLinkContainer) {
        backLinkContainer.classList.remove("show");
      }
    };

    // Gallery functionality
    const galleryImages = ref([]);
    const blogPostGallery = ref(null);

    const registerImage = (imageData) => {
      console.log("BlogPostPage: Registering image:", imageData);
      // Check if image already exists to avoid duplicates
      const exists = galleryImages.value.some(
        (img) => img.src === imageData.src
      );
      if (!exists) {
        galleryImages.value.push(imageData);
        console.log(
          "BlogPostPage: Total images registered:",
          galleryImages.value.length
        );
      }
    };

    const openGallery = (imageData) => {
      console.log("BlogPostPage: Opening gallery for image:", imageData);
      if (blogPostGallery.value) {
        const imageIndex = galleryImages.value.findIndex(
          (img) => img.src === imageData.src
        );
        console.log("BlogPostPage: Found image at index:", imageIndex);
        blogPostGallery.value.openGallery(imageIndex >= 0 ? imageIndex : 0);
      } else {
        console.warn("BlogPostPage: Gallery ref not available");
      }
    };

    return {
      post,
      parsedContent,
      renderMarkdown,
      tableOfContents,
      galleryImages,
      blogPostGallery,
      registerImage,
      openGallery,
    };
  },
};
</script>

<style>
.markdown-content {
  @apply text-gray-800 leading-relaxed text-[20px] max-w-4xl mx-auto;
  font-family: "Georgia", serif;
}

.markdown-content h1 {
  @apply text-5xl font-bold mt-12 mb-8 text-gray-900 leading-tight;
  font-family: "Outfit", sans-serif !important;
  scroll-margin-top: 120px;
}

.markdown-content h2 {
  @apply text-4xl font-bold mt-12 mb-6 text-gray-900 leading-tight;
  font-family: "Outfit", sans-serif !important;
  scroll-margin-top: 120px;
}

.markdown-content h3 {
  @apply text-2xl font-bold mt-8 mb-4 text-gray-800 leading-tight;
  font-family: "Outfit", sans-serif !important;
  scroll-margin-top: 120px;
}

.markdown-content h4,
.markdown-content h4 strong {
  @apply text-xl font-semibold mt-6 mb-3;
  font-family: "Outfit", sans-serif !important;
  color: rgb(16 185 129) !important;
  scroll-margin-top: 120px;
}

/* Align icon and text nicely in H4s */
.markdown-content h4 {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  line-height: 1.3;
}

.markdown-content p {
  @apply mb-8 text-[20px] leading-relaxed text-gray-700;
}

.markdown-content a {
  @apply text-blue-600 hover:text-blue-800 underline decoration-2 underline-offset-2;
}

/* Inline SVG icon list items */
.markdown-content .icon-li {
  width: 2rem;
  height: 2rem;
  vertical-align: -0.2em;
  display: inline-block;
  margin-right: 0.5rem;
  color: rgb(16 185 129); /* emerald-500 */
}

/* Header inline SVG size (e.g., Key Benefits / When to Use) */
.markdown-content h4 .h-icon {
  margin-top: 0.8rem;
  display: inline-block !important;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  position: relative;
  top: 0.02em;
  margin-right: 0.35rem;
  color: rgb(16 185 129); /* emerald-500 */
}

.markdown-content ul {
  @apply list-none mb-8 text-[20px] space-y-3;
  font-family: "Nunito", sans-serif !important;
}

.markdown-content ol {
  @apply list-decimal list-inside mb-8 text-[20px] space-y-3;
  font-family: "Nunito", sans-serif !important;
}

.markdown-content li {
  @apply mb-3 text-gray-700 leading-relaxed;
  font-family: "Nunito", sans-serif !important;
}

.markdown-content code {
  @apply bg-gray-100 rounded px-2 py-1 font-mono text-sm;
}

.markdown-content pre {
  @apply bg-gray-900 rounded-lg p-6 mb-8 overflow-x-auto border border-gray-700 relative;
  color: #e5e7eb;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 14px;
  line-height: 1.6;
  padding-top: 48px;
  padding-right: 80px;
}

.markdown-content pre code {
  @apply bg-transparent p-0 text-inherit;
  font-size: inherit;
}

/* Hide copy button on mobile devices */
@media (max-width: 768px) {
  .markdown-content pre .copy-button {
    display: none !important;
  }
}

.markdown-content blockquote {
  @apply border border-emerald-100 rounded-2xl px-6 md:px-8 pt-6 md:pt-8 pb-0 my-10 text-[20px] bg-white shadow-sm;
  font-family: "Georgia", serif !important;
  position: relative;
  transition: all 0.3s ease;
}

.markdown-content blockquote:hover {
  @apply shadow-[0_8px_30px_rgb(0,0,0,0.04)];
}

.markdown-content hr {
  @apply my-12 border-gray-300;
}

/* Enhanced list styling */
.markdown-content ul li::before {
  content: "•";
  @apply text-blue-500 font-bold;
  margin-right: 0.25rem;
  font-family: "Nunito", sans-serif !important;
}

/* Path arrow breadcrumb styling */
.path-arrow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin: 0.25rem 0;
  font-family: "Inter", sans-serif;
  font-size: 0.875rem;
  color: #374151;
  white-space: nowrap; /* Prevent wrapping */
  overflow-x: auto; /* Enable horizontal scrolling */
  max-width: 100%; /* Constrain to container width */
}

/* Mobile: Make breadcrumb horizontally scrollable */
@media (max-width: 768px) {
  .path-arrow {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    scrollbar-width: thin; /* Thin scrollbar on Firefox */
    scrollbar-color: #cbd5e1 #f1f5f9; /* Scrollbar colors */
  }

  /* Custom scrollbar for webkit browsers */
  .path-arrow::-webkit-scrollbar {
    height: 4px; /* Horizontal scrollbar height */
  }

  .path-arrow::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
  }

  .path-arrow::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }

  .path-arrow::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
}

.path-arrow .separator {
  color: #6b7280;
  font-weight: 500;
}

.path-arrow span:not(.separator) {
  background: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  cursor: pointer;
}

.path-arrow span:not(.separator):hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.markdown-content ul li {
  display: list-item;
  list-style: none;
  position: relative;
  padding-left: 0;
}

.markdown-content ul li::before {
  position: absolute;
  left: 0;
  top: 0;
}

.markdown-content ul li {
  padding-left: 1.5rem;
}

/* Hide bullet point when there's an emoji */
.markdown-content ul li:has(.emoji-lead)::before {
  display: none;
}

.markdown-content ul li:has(.emoji-lead) {
  padding-left: 0;
}

/* Spacing for wrapped leading emoji span */
.markdown-content .emoji-lead {
  margin-right: 0.25rem;
}

/* Add small gap after a leading emoji in list items */
/* first-letter spacing removed; handled by .emoji-lead */

@media (min-width: 768px) {
  .markdown-content ul li::before {
    margin-right: 0.75rem;
  }
  .markdown-content .emoji-lead {
    margin-right: 0.75rem;
  }
}

/* Special styling for emojis and highlights */
.markdown-content strong {
  @apply text-gray-900 font-semibold;
  display: inline;
  padding-right: 0;
}

/* Only add subtle spacing after bold text on desktop */
@media (min-width: 768px) {
  .markdown-content strong {
    display: inline;
    padding-right: 0;
  }
  .markdown-content strong::after {
    content: "\200A"; /* hair space */
  }
}

.markdown-content em {
  @apply text-gray-600 italic;
}

/* Path styling - looks like file explorer navigation */
.markdown-content .path {
  @apply bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 font-mono text-sm text-gray-700;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  position: relative;
  display: inline-block;
}

.markdown-content .path::before {
  content: "📁";
  margin-right: 8px;
  font-size: 14px;
}

/* Alternative: Arrow-based path separator */
.markdown-content .path-arrow {
  @apply bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 font-mono text-sm text-blue-700;
  font-family: "Monaco", "Monaco", "Ubuntu Mono", monospace;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Bleeding image styles for smaller Method 2 images */
.markdown-content .image-bleed-left {
  float: left !important;
  margin: 1rem 1.5rem 1rem -2rem !important;
  max-width: 45% !important;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  clear: none !important;
}

.markdown-content .image-bleed-right {
  float: right !important;
  margin: 1rem -2rem 1rem 1.5rem !important;
  max-width: 45% !important;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  clear: none !important;
}

/* Force text to wrap around images */
.markdown-content p:has(+ .simple-image-container .image-bleed-left),
.markdown-content p:has(+ .simple-image-container .image-bleed-right) {
  overflow: visible !important;
}

/* Alternative: Target paragraphs that come after bleeding images */
.markdown-content .simple-image-container:has(.image-bleed-left) + p,
.markdown-content .simple-image-container:has(.image-bleed-right) + p {
  overflow: visible !important;
  clear: none !important;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .markdown-content .image-bleed-left,
  .markdown-content .image-bleed-right {
    float: none !important;
    margin: 1rem 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }
}

.markdown-content .path-arrow .separator {
  color: #6b7280;
  font-weight: bold;
}

/* Enhanced article metadata styling */
.article-meta {
  @apply text-lg sm:text-xl text-gray-600 font-medium;
}

/* Enhanced article title styling */
.article-meta + h1 {
  @apply text-5xl sm:text-6xl font-bold text-gray-900 mb-8 leading-tight;
}

/* Add top padding for fixed back link header - only on mobile */
@media (max-width: 1024px) {
  .blog-post-container {
    padding-top: 4rem !important; /* Account for fixed back link header on mobile only */
  }
}

/* Ensure the main article title uses Outfit */
.blog-post-container article > h1 {
  font-family: "Outfit", sans-serif !important;
}

/* Enhanced image styling */
.article-meta + h1 + img {
  @apply w-full h-auto object-cover rounded-xl mb-12 shadow-lg;
}

/* Two-column layout for blog content */
.blog-content-wrapper {
  display: block;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 2rem; /* Original desktop padding */
  transition: padding-right 0.3s ease; /* Smooth transition for responsive changes */
}

.blog-article {
  max-width: 100%;
  margin: 0 auto;
  transition: max-width 0.3s ease; /* Smooth transition for content width changes */
}

/* Desktop: Original padding for comfortable reading */
@media (min-width: 1025px) {
  .blog-content-wrapper {
    padding: 0 2rem; /* Original desktop padding */
  }

  .blog-article {
    padding: 0 2rem; /* Original desktop padding */
  }
}

/* Mobile: Reduce padding for better space usage */
@media (max-width: 1024px) {
  .blog-content-wrapper {
    padding: 0 0.25rem; /* Minimal padding on mobile */
  }

  .blog-article {
    padding: 0 0.125rem; /* Very minimal padding for article content on mobile */
  }
}

.toc-sidebar {
  /* Don't override TOC component positioning - let it handle its own positioning */
}

/* Klaviyo form positioned at bottom right */
.klaviyo-sidebar {
  position: fixed;
  bottom: 2rem; /* Position at bottom */
  right: 2rem;
  z-index: 99; /* Slightly lower than TOC */
  width: 320px; /* Match TOC width exactly */
  background: white;
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;
  max-height: 500px; /* Prevent form from getting too tall */
}

/* Klaviyo form container styling */
.klaviyo-form-container {
  width: 100%;
  padding: 0; /* Remove padding since it's in its own container */
  margin: 0; /* Remove margin since it's in its own container */
  isolation: isolate; /* Create new stacking context */
  max-height: 500px; /* Limit the height */
  overflow-y: auto; /* Add scroll if needed */
}

.klaviyo-form-container :deep(.klaviyo-form-content) {
  margin: 0 !important;
}

.klaviyo-form-container :deep(.klaviyo-form-XCcL6a) {
  width: 100% !important;
  max-width: 100% !important;
  position: static !important;
  z-index: auto !important;
  transform: none !important;
  left: auto !important;
  right: auto !important;
  top: auto !important;
  bottom: auto !important;
  margin: 0 !important;
  box-shadow: none !important;
}

/* Form positioning when TOC is toggled */
.klaviyo-form-container.form-moved-down {
  margin-top: 0;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

/* Additional form containment merged with main definition */

/* TOC sidebar stacking properties merged with main definition */

.klaviyo-form-container :deep(.klaviyo-form-XCcL6a) {
  contain: layout style paint; /* CSS containment */
}

/* Target all Klaviyo form elements to prevent overlay */
.klaviyo-form-container :deep(.klaviyo-form-XCcL6a *) {
  position: static !important;
  z-index: auto !important;
  transform: none !important;
  left: auto !important;
  right: auto !important;
  top: auto !important;
  bottom: auto !important;
}

/* Specifically target any modal or overlay elements */
.klaviyo-form-container :deep(.klaviyo-form-XCcL6a [class*="modal"]),
.klaviyo-form-container :deep(.klaviyo-form-XCcL6a [class*="overlay"]),
.klaviyo-form-container :deep(.klaviyo-form-XCcL6a [class*="popup"]) {
  display: none !important;
}

/* Responsive adjustments for TOC */
@media (max-width: 1509px) {
  .blog-content-wrapper {
    padding-right: 360px; /* Leave space for TOC (320px + 40px margin) */
  }

  .toc-sidebar {
    right: 1rem; /* Reduce right margin on smaller screens */
  }

  .klaviyo-sidebar {
    right: 1rem; /* Match TOC positioning */
    width: 320px; /* Ensure consistent width */
  }
}

@media (max-width: 1200px) {
  .blog-content-wrapper {
    padding-right: 340px; /* Slightly less padding */
  }

  .toc-sidebar {
    right: 0.5rem;
  }

  .klaviyo-sidebar {
    right: 0.5rem; /* Match TOC positioning */
    width: 320px; /* Ensure consistent width */
  }
}

@media (max-width: 1024px) {
  .blog-content-wrapper {
    padding: 0 2rem; /* Reset padding on mobile */
  }

  .toc-sidebar {
    display: none; /* Hide TOC on mobile */
  }

  .klaviyo-sidebar {
    /* Show Klaviyo form in document flow on mobile */
    position: static; /* Change from fixed to static */
    width: auto; /* Full width minus margins */
    max-height: none; /* Remove height restriction */
    margin: 2rem 1rem; /* Add margin for spacing */
    z-index: auto; /* Reset z-index */
  }
}

/* Lazy loading styles for markdown images */
.lazy-image-wrapper {
  position: relative;
  width: 100%;
  margin: 2rem 0;
  min-height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.lazy-image-wrapper.loading {
  animation: loading 1.5s infinite;
}

.lazy-image-wrapper.loaded {
  background: transparent;
  min-height: auto;
}

.lazy-image-wrapper img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.lazy-image-wrapper.loaded img {
  opacity: 1;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Responsive image loading states */
@media (max-width: 768px) {
  .lazy-image-wrapper {
    min-height: 150px;
  }
}

/* Enhanced loading animations */
.lazy-image-wrapper::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  opacity: 0.7;
}

.lazy-image-wrapper.loaded::before {
  display: none;
}

/* Progressive image loading effect */
.lazy-image-wrapper img {
  transition: opacity 0.5s ease, transform 0.3s ease;
}

.lazy-image-wrapper.loaded img {
  transform: scale(1);
}

.lazy-image-wrapper img:not(.loaded) {
  transform: scale(1.02);
}

/* Loading skeleton improvements */
.lazy-image-wrapper.loading {
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Back to Blog Link Styling */
.back-link-container {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 200 !important; /* Higher than TOC */
  background: white !important;
  border-bottom: 1px solid #e5e7eb !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
  max-width: 100% !important;
  margin: 0 auto !important;
  padding: 1rem 2rem !important;
  transition: all 0.3s ease !important; /* Smooth transitions */
  transform: translateY(-100%) !important; /* Hidden by default */
  opacity: 0 !important;
}

.back-link-container.show {
  transform: translateY(0) !important; /* Show when needed */
  opacity: 1 !important;
}

/* Desktop: Show normally (not fixed) */
@media (min-width: 1025px) {
  .back-link-container {
    position: static !important; /* Normal flow on desktop */
    transform: none !important; /* Always visible */
    opacity: 1 !important;
    background: transparent !important; /* No background on desktop */
    border-bottom: none !important; /* No border on desktop */
    box-shadow: none !important; /* No shadow on desktop */
    padding: 2rem !important; /* Normal padding on desktop */
    margin: 0 auto !important;
    max-width: 100% !important;
  }

  .blog-post-container {
    padding-top: 0 !important; /* No extra padding needed on desktop */
  }
}

/* Mobile: Smart fixed behavior */
@media (max-width: 1024px) {
  .back-link-container {
    display: block !important;
  }

  .blog-post-container {
    padding-top: 4rem !important; /* Account for fixed back link header on mobile only */
  }
}

.back-link {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  color: #22c55e !important; /* Green color to match TOC active state */
  text-decoration: none !important;
  font-weight: 500 !important;
  font-size: 1.125rem !important; /* text-lg equivalent */
  transition: color 0.2s ease !important;
}

.back-link:hover {
  color: #15803d !important; /* Darker green on hover */
}

.back-link svg {
  width: 20px !important;
  height: 20px !important;
  transition: transform 0.2s ease !important;
}

.back-link:hover svg {
  transform: translateX(-4px) !important;
}

/* Responsive adjustments for back link to match article content */
@media (max-width: 1509px) {
  .back-link-container {
    padding-right: 320px !important; /* Match article padding */
  }
}

@media (max-width: 1200px) {
  .back-link-container {
    padding-right: 300px !important; /* Match article padding */
  }
}

@media (max-width: 1024px) {
  .back-link-container {
    padding: 2rem !important; /* Reset padding on mobile */
  }
}
</style>
